import { Info } from '@phosphor-icons/react/dist/ssr/Info'
import { useEffect, useId, useRef, useState } from 'react'

type Props = {
  /** Visible label, e.g. for `aria-label` (“What Case build time means”). */
  label: string
  explanation: string
  className?: string
}

/**
 * Gray info control: hover/focus shows gloss; tap toggles (for touch). Closes on outside click or Escape.
 */
export const HeroAsideLabelTip = ({ label, explanation, className }: Props) => {
  const id = useId()
  const rootRef = useRef<HTMLSpanElement>(null)
  const [hover, setHover] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [focused, setFocused] = useState(false)
  const visible = hover || pinned || focused

  useEffect(() => {
    if (!pinned) {
      return
    }
    const close = (e: PointerEvent) => {
      if (rootRef.current?.contains(e.target as Node)) {
        return
      }
      setPinned(false)
    }
    const tid = window.setTimeout(() => document.addEventListener('pointerdown', close), 0)
    return () => {
      window.clearTimeout(tid)
      document.removeEventListener('pointerdown', close)
    }
  }, [pinned])

  return (
    <span
      ref={rootRef}
      className={['relative inline-flex shrink-0 align-middle self-center', className].filter(Boolean).join(' ')}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        type="button"
        className="-m-px inline-flex items-center justify-center rounded-full p-px text-[#838383] transition-colors hover:text-[#636363] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-1"
        aria-label={`What “${label}” means`}
        aria-describedby={visible ? id : undefined}
        aria-expanded={visible}
        aria-controls={id}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={(e) => {
          e.stopPropagation()
          setPinned((p) => !p)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setPinned(false)
          }
        }}
      >
        <Info size={13} weight="regular" aria-hidden />
      </button>
      <span
        id={id}
        role="tooltip"
        className={`absolute left-0 top-full z-30 mt-1.5 w-[min(calc(100vw-2rem),14rem)] rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] px-2.5 py-2 text-left text-[11px] font-normal normal-case leading-snug tracking-[0.01em] whitespace-normal text-[var(--foreground)] shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-[opacity,visibility] duration-150 [text-wrap:pretty] before:absolute before:bottom-full before:left-0 before:h-2 before:w-full before:content-[''] ${
          visible
            ? 'pointer-events-auto visible opacity-100'
            : 'pointer-events-none invisible opacity-0'
        }`}
      >
        {explanation}
      </span>
    </span>
  )
}
